import { useContext, useEffect, useState } from "react"
import { UserContext } from "../../context/userContext"
import { useLazyQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import { ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE } from "./queries"
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import customStyles from "../../styles/customDataForms/CustomDataForms.module.css"
import CustomTable from "../../components/CustomTable/CustomTable"
import { formatToLocalDate } from "../../helpers/dateConversion"
import LoaderFullPage from "../../components/Loaders"
import StandardPageHeader from "../../components/StandardPageHeader/StandardPageHeader"
import _ from "lodash"

export default function Engagements() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const userCtx = useContext(UserContext)
  const [typesFilter, setTypesFilter] = useState(null)
  const [organizationFilter, setOrganizationFilter] = useState(null)
  const [locationFilter, setLocationFilter] = useState(null)

  const ID_TEMPLATES = [
    "brief_check_in",
    "care_coordination_session",
    "recovery_plan_engagement",
    "group_session",
    "intake_session",
    "discharge_session",
    "intake_reactivation_session",
  ]

  const ID_BLOCKS = [
    "session",
    "referral",
    "session__assessment_recovery_capital",
  ]

  const mapOrganization = (organization) => {
    if (!organization) {
      return "Unknown Organization"
    }
    if (organization === false) {
      return "Unknown Organization"
    }
    return organization
  }

  const [getData] = useLazyQuery(ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE, {
    variables: {
      participant_id: userCtx.participant_id,
      idTemplates: ID_TEMPLATES,
      idBlocks: ID_BLOCKS,
    },
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      if (data.engagementsParticipant === null) {
        setData(null)
        return setLoading(false)
      }
      const dataFormatted = JSON.parse(data.engagementsParticipant.data)
      const handleData = () =>
        _.map(dataFormatted, (template) => {
          const blocks = _.groupBy(template, "block_name")
          const session = _.isEmpty(blocks?.session) ? {} : blocks?.session[0]

          const handleLocations = () => {
            const locationsList = _.map(
              session?.values?.session__location_ids,
              ({ label }) => label,
            )
            return _.join(locationsList, ", ")
          }

          return {
            organization: mapOrganization(session?.organization_name),
            date: new Date(session?.values?.session_date),
            locations: handleLocations(),
            engagement_type: _.startCase(
              session?.idTemplate?.replace(/_/g, " "),
            ),
            length: `${session?.values?.session_length} minutes`,
            facilitating_staff: _.unescape(session?.staff_full_name),
            view: (
              <Link
                to={`/form/view/${session.organization_id}/${session?.idTemplate}/${session?.idStamp}`}
                className={customStyles.actionButton}
                state={{
                  previousTitle: "Engagements",
                  previousRoute: "/participant/engagements",
                }}
              >
                View
              </Link>
            ),
          }
        })
      setData(handleData())

      const allTypes = _.map(
        handleData(),
        ({ engagement_type }) => engagement_type || "",
      )
      setTypesFilter(_.uniq(allTypes).sort())

      const allLocations = _.map(handleData(), ({ locations }) => locations)
      if (
        !allLocations.includes("Not set") &&
        (allLocations.includes(undefined) ||
          allLocations.includes(null) ||
          allLocations.includes(""))
      ) {
        allLocations.push("Not set")
      }
      setLocationFilter(_.uniq(allLocations).sort())

      const allOrganizations = _.map(
        handleData(),
        ({ organization }) => organization,
      )
      setOrganizationFilter(_.uniq(allOrganizations).sort())

      setData(handleData())

      setLoading(false)
    },
  })

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filters = () => {
    return [
      {
        value: "organization",
        label: "Organization",
        options: organizationFilter,
        placeholder: "Select organization",
        type: "multiselect",
      },
      {
        value: "location",
        label: "Location",
        options: locationFilter,
        placeholder: "Select location",
        showClear: true,
        type: "dropdown",
      },
      {
        value: "engagement_type",
        label: "Type",
        options: typesFilter,
        placeholder: "Select type",
        showClear: true,
        type: "dropdown",
      },
    ]
  }

  const sorting = () => {
    return [
      {
        value: "sortBy",
        label: "Sort by",
        options: [
          { name: "Engagement date", code: "engagementDate" },
          { name: "Staff name", code: "staff" },
          { name: "Organization", code: "organization" },
        ],
        default: "engagementDate",
        placeholder: "Select option",
        showClear: true,
        type: "multiselect",
      },
    ]
  }

  const headers = () => {
    return [
      {
        value: "engagementDate",
        label: "Engagement Date",
        size: 2,
      },
      {
        value: "engagement_type",
        label: "Engagement Type",
        size: 2,
      },
      {
        value: "length",
        label: "Engagement Duration",
        size: 2,
      },
      {
        value: "facilitating_staff",
        label: "Staff ",
        size: 2,
      },
      {
        value: "organization",
        label: "Organization",
        size: 2,
      },
      {
        value: "location",
        label: "Location",
        size: 1,
      },
      {
        value: "view",
        label: "",
        action: true,
        size: 1,
      },
    ]
  }

  const mapLocation = (locations) => {
    if (!locations) {
      return "Not set"
    }
    if (locations === false) {
      return "Not set"
    }
    return locations
  }

  const mapData = () => {
    return data?.map((d) => {
      return {
        organization: d.organization,
        location: mapLocation(d.locations),
        engagement_type: d.engagement_type,
        engagementDate: formatToLocalDate(d.date),
        length: d.length,
        facilitating_staff: d.facilitating_staff,
        view: d.view,
      }
    })
  }

  return (
    <div>
      <div className="main-content">
        <AppBreadcrumb items={[{ label: "Engagements" }]} />
        <div className="w-full pb-5">
          <StandardPageHeader
            title="Engagements"
            refreshData={() => {
              setLoading(true)
              getData()
            }}
          />

          <hr className="line" />

          {loading ? (
            <LoaderFullPage />
          ) : (
            <CustomTable
              headers={headers()}
              collection={mapData()}
              filters={filters()}
              sorting={sorting()}
            />
          )}
        </div>
      </div>
    </div>
  )
}
